import React from 'react'
import { FontSizeControl } from '../../../controls/font-size-control'
import { LetterSpacingControl } from '../../../controls/letter-spacing-control'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { LineHeightControl } from '../../../controls/line-height-control'
import { FontSelectorControl } from '../../../controls/font-selector-control'
import { TextAlignControl } from '../../../controls/text-align-control'
import { twMerge } from 'tailwind-merge'
import { AlignType } from 'common/types/styleTypes'
import { useFontSelector } from '../hooks/use-font-selector'
import {
  WebsiteFontSettingsInterface,
  WebsiteSettingsTypographyTypeEnum,
  WebsiteThemeTextInterface,
} from 'src/shared/types/website-settings-types'
import {
  useUpdateStoreObject,
  useUpdateStoreObjectProp,
} from 'src/editor/shared/hooks/use-update-store'
import { useTypography } from 'src/editor/shared/hooks/use-typography'
import { calculateShowComponents } from '../utils'
import { ShowHideTypographyControlsType } from '../types'
import { ColorPickerGroup } from '../../../groups/color-picker'

type TypographyHook = (arg: {
  fontSize?: number
  mobileFontSize?: number
  lineHeight?: number
  mobileLineHeight?: number
  textAlign?: AlignType
  mobileTextAlign?: AlignType
  letterSpacing?: number
  mobileLetterSpacing?: number
  typographyType?: WebsiteSettingsTypographyTypeEnum
}) => {
  calculatedFontSize: {
    mobile: number | undefined
    desktop: number | undefined
  }
  calculatedLineHeight: {
    mobile: number | undefined
    desktop: number | undefined
  }
  calculatedTextAlign: {
    mobile: AlignType | undefined
    desktop: AlignType | undefined
  }
  calculatedLetterSpacing: {
    mobile: number | undefined
    desktop: number | undefined
  }
  globalFontFamily: string | undefined
}

export type UpdateTypographyAction<
  TypographyObject extends WebsiteFontSettingsInterface,
> = (typography: TypographyObject) => {
  type: string
  payload: TypographyObject
}

type WebsiteTypographyGroupProps<
  TypographyObject extends WebsiteFontSettingsInterface,
> = {
  typographyObject: TypographyObject
  updateTypographyAction: UpdateTypographyAction<TypographyObject>
  typographyHook?: TypographyHook
  typographyType?: WebsiteSettingsTypographyTypeEnum
  title?: string
  hideTitle?: boolean
  isFirst?: boolean
  isRootSetting?: boolean
  colorKeys?: {
    key: keyof WebsiteThemeTextInterface
    title: string
  }[]
} & ShowHideTypographyControlsType

export const WebsiteTypographyGroup = <
  TypographyObject extends WebsiteFontSettingsInterface,
>({
  typographyObject,
  updateTypographyAction,
  typographyHook = useTypography,
  typographyType,
  title,
  hideTitle,
  isFirst,
  isRootSetting,
  showFontSelector: showFontSelectorFromProps,
  showFontSize: showFontSizeFromProps,
  showLineHeight: showLineHeightFromProps,
  showTextAlign: showTextAlignFromProps,
  showLetterSpacing: showLetterSpacingFromProps,
  hideFontSelector,
  hideFontSize,
  hideLineHeight,
  hideTextAlign,
  hideLetterSpacing,
  colorKeys,
}: WebsiteTypographyGroupProps<TypographyObject>) => {
  const [
    showFontSelector,
    showFontSize,
    showLineHeight,
    showTextAlign,
    showLetterSpacing,
  ] = calculateShowComponents(
    [
      showFontSelectorFromProps,
      showFontSizeFromProps,
      showLineHeightFromProps,
      showTextAlignFromProps,
      showLetterSpacingFromProps,
    ],
    [
      hideFontSelector,
      hideFontSize,
      hideLineHeight,
      hideTextAlign,
      hideLetterSpacing,
    ],
  )

  const {
    fontSize,
    mobileFontSize,
    fontFamily,
    mobileFontFamily,
    fontWeight,
    mobileFontWeight,
    fontStyle,
    mobileFontStyle,
    lineHeight,
    mobileLineHeight,
    letterSpacing,
    mobileLetterSpacing,
    textAlign,
    mobileTextAlign,
  } = typographyObject

  const updateTypographyProp = useUpdateStoreObjectProp(
    typographyObject,
    updateTypographyAction,
  )
  const updateTypography = useUpdateStoreObject(updateTypographyAction)

  const { inheritedFontTypeLabel, updateObjectFont } = useFontSelector({
    object: typographyObject,
    fontFamilyKey: 'fontFamily',
    fontWeightKey: 'fontWeight',
    fontStyleKey: 'fontStyle',
    fontFileIdKey: 'fontFileId',
    updateObject: updateTypography,
  })

  const {
    calculatedFontSize,
    calculatedLineHeight,
    calculatedTextAlign,
    calculatedLetterSpacing,
    globalFontFamily,
  } = typographyHook({
    fontSize,
    mobileFontSize,
    lineHeight,
    mobileLineHeight,
    textAlign,
    mobileTextAlign,
    letterSpacing,
    mobileLetterSpacing,
    typographyType,
  })

  return (
    <>
      {!hideTitle && (
        <GroupTitle isFirst={isFirst}>{title || 'typography'}</GroupTitle>
      )}
      <div
        className={twMerge(
          '[&>*]:!m-0 mt-4 grid grid-cols-2 gap-3',
          '[&>*:nth-child(1)]:col-span-2 [&>*:nth-child(2)]:col-span-2',
        )}
      >
        {showFontSelector && (
          <FontSelectorControl
            update={updateObjectFont}
            fontFamily={fontFamily}
            mobileFontFamily={mobileFontFamily}
            fontWeight={fontWeight}
            mobileFontWeight={mobileFontWeight}
            fontStyle={fontStyle}
            mobileFontStyle={mobileFontStyle}
            hideLabel
            isRootSetting={isRootSetting}
            globalFontFamily={globalFontFamily}
            inheritedFontTypeLabel={inheritedFontTypeLabel}
          />
        )}
        {showFontSize && (
          <FontSizeControl
            fontSize={calculatedFontSize.desktop}
            mobileFontSize={calculatedFontSize.mobile}
            update={updateTypographyProp('fontSize')}
            mobileUpdate={updateTypographyProp('mobileFontSize')}
            className="!col-span-1"
          />
        )}
        {showLineHeight && (
          <LineHeightControl
            lineHeight={calculatedLineHeight.desktop}
            mobileLineHeight={calculatedLineHeight.mobile}
            update={updateTypographyProp('lineHeight')}
            mobileUpdate={updateTypographyProp('mobileLineHeight')}
            className="!col-span-1"
          />
        )}
        {showTextAlign && (
          <TextAlignControl
            textAlign={calculatedTextAlign.desktop}
            mobileTextAlign={calculatedTextAlign.mobile}
            update={updateTypographyProp('textAlign')}
            mobileUpdate={updateTypographyProp('mobileTextAlign')}
            className="!col-span-1"
          />
        )}
        {showLetterSpacing && (
          <LetterSpacingControl
            letterSpacing={calculatedLetterSpacing.desktop}
            mobileLetterSpacing={calculatedLetterSpacing.mobile}
            update={updateTypographyProp('letterSpacing')}
            mobileUpdate={updateTypographyProp('mobileLetterSpacing')}
            wrapperClassName="!col-span-2"
          />
        )}
      </div>
      {colorKeys && (
        <ColorPickerGroup elementKey="text" colorKeys={colorKeys} />
      )}
    </>
  )
}
